import { useMemo, type ReactNode } from 'react';
import { API_KEY_SCOPES, WEBHOOK_EVENT_TYPES } from '@wave/shared';
import { btnGhostSm, cardL1, sectionTitle, tableCell, tableCls, tableHead, tableHeadCell, tableRow, tableWrapper } from '../../lib/styles';
import { SCOPE_LABELS, WEBHOOK_EVENT_LABELS } from './labels';
import {
  CREATE_ORDER_FIELDS,
  ERROR_CODES,
  SCOPE_ACCESS,
  WEBHOOK_EVENT_DESCRIPTIONS,
  WEBHOOK_HEADERS,
  accountsCurl,
  accountsResponseJson,
  badRequestErrorJson,
  createOrderCurl,
  createOrderResponseJson,
  currenciesCurl,
  currenciesResponseJson,
  getOrderByExternalCurl,
  getOrderCurl,
  idempotentRetryCurl,
  orderNotFoundJson,
  pingCurl,
  pingResponseJson,
  rateLimitErrorJson,
  usdtRatesCurl,
  usdtRatesResponseJson,
  validationErrorJson,
  webhookPayloadJson,
  webhookVerifyNodeJs,
} from './apiDocsContent';

const TOC: Array<{ id: string; label: string }> = [
  { id: 'overview', label: 'Обзор и аутентификация' },
  { id: 'ping', label: 'Проверка ключа' },
  { id: 'create-order', label: 'Создание заявки' },
  { id: 'idempotency', label: 'Идемпотентность' },
  { id: 'reading', label: 'Чтение' },
  { id: 'references', label: 'Справочники' },
  { id: 'errors', label: 'Ошибки и лимиты' },
  { id: 'webhooks', label: 'Webhooks' },
];

/** Моноширинный блок кода с кнопкой копирования (паттерн AdminUsersPage). */
function CodeBlock({ code }: { code: string }) {
  return (
    <div className="group relative">
      <pre className="overflow-x-auto rounded-md border border-border bg-bg3 p-3 font-mono text-xs text-text">
        {code}
      </pre>
      <button
        type="button"
        onClick={() => void navigator.clipboard.writeText(code)}
        className={`${btnGhostSm} absolute right-2 top-2 bg-bg2`}
        title="Скопировать"
      >
        ⧉
      </button>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className={`${cardL1} scroll-mt-4`}>
      <h2 className={sectionTitle}>{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-text2">{children}</div>
    </section>
  );
}

function InlineCode({ children }: { children: string }) {
  return <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[12px] text-text">{children}</code>;
}

function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-3 py-2 text-sm text-warning">
      {children}
    </div>
  );
}

/**
 * Вкладка «Документация» страницы /admin/api (задача 9). Самодостаточная
 * рукописная документация публичного API приёма заявок — для внешних
 * интеграторов. Base URL считается динамически от текущего origin.
 */
export function ApiDocsTab() {
  const base = useMemo(() => `${window.location.origin}/api/v1/public`, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-lg border border-border bg-bg2 px-4 py-3 text-xs">
        {TOC.map((item) => (
          <a key={item.id} href={`#${item.id}`} className="text-accent hover:underline">
            {item.label}
          </a>
        ))}
      </div>

      <Section id="overview" title="1. Обзор и аутентификация">
        <p>
          Публичный API позволяет внешним сервисам создавать и читать заявки Wave Panel напрямую, без участия
          оператора. Все эндпоинты — под базовым URL:
        </p>
        <CodeBlock code={base} />
        <p>
          Аутентификация — заголовком <InlineCode>Authorization</InlineCode> с ключом вида{' '}
          <InlineCode>wave_sk_...</InlineCode>:
        </p>
        <CodeBlock code={'Authorization: Bearer wave_sk_live_9f3a1c7de2b84f0a9c02e'} />
        <p>
          Ключи создаются и отзываются на вкладке «Ключи» этой же страницы. Ключ показывается в открытом виде один
          раз — сразу после создания; сервер хранит только его хэш и восстановить значение позже нельзя (только
          перевыпустить).
        </p>
        <p>Каждый ключ имеет набор скоупов — доступ к эндпоинтам проверяется именно по ним:</p>
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Скоуп</th>
                <th className={tableHeadCell}>Название</th>
                <th className={tableHeadCell}>Даёт доступ к</th>
              </tr>
            </thead>
            <tbody>
              {API_KEY_SCOPES.map((s) => (
                <tr key={s} className={tableRow}>
                  <td className={tableCell}>
                    <InlineCode>{s}</InlineCode>
                  </td>
                  <td className={tableCell}>{SCOPE_LABELS[s]}</td>
                  <td className={tableCell}>{SCOPE_ACCESS[s]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="ping" title="2. Проверка ключа">
        <p>
          <InlineCode>GET /ping</InlineCode> (скоуп <InlineCode>refs:read</InlineCode>) — быстрый способ проверить,
          что ключ валиден и посмотреть его скоупы. Ничего не создаёт и не читает из бизнес-данных.
        </p>
        <CodeBlock code={pingCurl(base)} />
        <CodeBlock code={pingResponseJson} />
      </Section>

      <Section id="create-order" title="3. Создание заявки">
        <p>
          <InlineCode>POST /orders</InlineCode> (скоуп <InlineCode>orders:create</InlineCode>) — создаёт заявку с
          теми же четырьмя ногами и валидациями, что и внутренняя форма создания заявки. Отличия от внутреннего API:{' '}
          <InlineCode>client</InlineCode>/<InlineCode>executor</InlineCode>/<InlineCode>operator</InlineCode> можно не
          передавать — не переданные поля подставляются из дефолтов ключа (задаются в модалке ключа на вкладке
          «Ключи»), и добавлено поле <InlineCode>externalId</InlineCode> для идемпотентности.
        </p>
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Поле</th>
                <th className={tableHeadCell}>Тип</th>
                <th className={tableHeadCell}>Обязательность</th>
                <th className={tableHeadCell}>Описание</th>
              </tr>
            </thead>
            <tbody>
              {CREATE_ORDER_FIELDS.map((f) => (
                <tr key={f.field} className={tableRow}>
                  <td className={tableCell}>
                    <InlineCode>{f.field}</InlineCode>
                  </td>
                  <td className={`${tableCell} whitespace-nowrap text-text2`}>{f.type}</td>
                  <td className={`${tableCell} whitespace-nowrap`}>
                    {f.required === 'обязательно' ? (
                      <span className="text-text">обязательно</span>
                    ) : (
                      <span className="text-text-subtle">опционально</span>
                    )}
                  </td>
                  <td className={tableCell}>{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p>
          <InlineCode>confirmAll</InlineCode> (требует скоуп <InlineCode>orders:confirm</InlineCode> дополнительно к{' '}
          <InlineCode>orders:create</InlineCode>) — если у интегратора к моменту создания заявки все движения денег
          уже свершились, передайте <InlineCode>confirmAll: true</InlineCode>, и заявка родится сразу{' '}
          <strong>закрытой</strong>: все 4 проводки подтверждаются в момент создания (<InlineCode>status</InlineCode>{' '}
          в ответе — <InlineCode>closed</InlineCode>, все <InlineCode>provodki</InlineCode> — <InlineCode>done</InlineCode>).
          Если у ключа нет скоупа <InlineCode>orders:confirm</InlineCode> — <InlineCode>403</InlineCode>, и заявка{' '}
          <strong>не создаётся</strong>. При идемпотентном повторе (заявка с этим <InlineCode>externalId</InlineCode>{' '}
          уже создана) <InlineCode>confirmAll</InlineCode> игнорируется — заявка возвращается как есть (
          <InlineCode>200</InlineCode>). Если у ключа настроены вебхуки, придут четыре{' '}
          <InlineCode>order.provodka.confirmed</InlineCode> и один итоговый <InlineCode>order.status.changed</InlineCode>.
        </p>

        <p className="mt-4 font-medium text-text">Пример запроса</p>
        <p>
          Клиент отдаёт 100 000 ₽, получает ≈1180 USDT; исполнитель отдаёт клиенту эти же 1180 USDT, а взамен
          получает 97 000 ₽ — разница между ногами (в пересчёте в USDT по курсу снапшота) и есть профит компании.
        </p>
        <CodeBlock code={createOrderCurl(base)} />

        <p className="mt-4 font-medium text-text">
          Ответ — <InlineCode>201</InlineCode> при первом создании, <InlineCode>200</InlineCode> при повторном
          вызове с тем же externalId (см. «Идемпотентность»)
        </p>
        <p>
          Профит (<InlineCode>profitAmount</InlineCode>/<InlineCode>profitCcy</InlineCode>) и снапшот курсов —
          внутренние поля компании, наружу не отдаются. Проводки (<InlineCode>provodki</InlineCode>) урезаны до
          того, что нужно для отслеживания исполнения: индекс ноги, счёт, валюта, сумма (со знаком), статус,
          время подтверждения.
        </p>
        <CodeBlock code={createOrderResponseJson} />
      </Section>

      <Section id="idempotency" title="4. Идемпотентность">
        <p>
          Если передан <InlineCode>externalId</InlineCode>, он проверяется в паре с ключом:{' '}
          <InlineCode>(keyId, externalId)</InlineCode> уникальны. Повторный вызов{' '}
          <InlineCode>POST /orders</InlineCode> с уже использованным <InlineCode>externalId</InlineCode> НЕ создаёт
          вторую заявку — сервер возвращает ранее созданную заявку с кодом <InlineCode>200</InlineCode> (вместо{' '}
          <InlineCode>201</InlineCode>). Тело повторного запроса не сверяется с исходным — ответственность за
          консистентность на стороне интегратора (обычно один externalId = один POST с одним и тем же телом,
          например для повторов после таймаута сети).
        </p>
        <CodeBlock code={idempotentRetryCurl(base)} />
        <Note>
          <InlineCode>externalId</InlineCode> уникален в рамках одного ключа. Два разных ключа могут использовать
          одинаковый externalId независимо — это разные namespace.
        </Note>
      </Section>

      <Section id="reading" title="5. Чтение заявок">
        <p>
          <InlineCode>GET /orders/:id</InlineCode> и <InlineCode>GET /orders/by-external/:externalId</InlineCode>{' '}
          (скоуп <InlineCode>orders:read</InlineCode>) читают заявку — но только если она была создана этим же
          ключом. Заявка другого ключа или несуществующий id/externalId — в обоих случаях{' '}
          <InlineCode>404 not_found</InlineCode> (намеренно, а не 403 — наружу не раскрывается сам факт
          существования чужой заявки).
        </p>
        <CodeBlock code={getOrderCurl(base)} />
        <CodeBlock code={getOrderByExternalCurl(base)} />
        <p className="font-medium text-text">404 — заявка не найдена или принадлежит другому ключу</p>
        <CodeBlock code={orderNotFoundJson} />
      </Section>

      <Section id="references" title="6. Справочники">
        <p>
          Три read-only справочника (скоуп <InlineCode>refs:read</InlineCode>) — пригодятся, чтобы заранее
          провалидировать коды счетов/валют и посчитать сумму по курсу на своей стороне перед отправкой заявки.
        </p>

        <p className="font-medium text-text">
          <InlineCode>GET /accounts</InlineCode> — только активные счета
        </p>
        <CodeBlock code={accountsCurl(base)} />
        <CodeBlock code={accountsResponseJson} />
        <p className="text-text-subtle">
          <InlineCode>telegramChatId</InlineCode> — строка или <InlineCode>null</InlineCode>; Telegram chat_id
          привязанного чата — для автоматического маппинга счетов.
        </p>

        <p className="mt-3 font-medium text-text">
          <InlineCode>GET /currencies</InlineCode>
        </p>
        <CodeBlock code={currenciesCurl(base)} />
        <CodeBlock code={currenciesResponseJson} />
        <p className="text-xs">
          <InlineCode>scale</InlineCode> — желаемая точность отображения валюты. Хранение денег в системе — всегда{' '}
          <InlineCode>numeric(20,4)</InlineCode>, поэтому фактически в ответах API суммы не имеют больше 4 знаков
          после запятой, даже для USDT (scale 8).
        </p>

        <p className="mt-3 font-medium text-text">
          <InlineCode>GET /usdt-rates</InlineCode> — текущие (live) курсы к USDT
        </p>
        <CodeBlock code={usdtRatesCurl(base)} />
        <CodeBlock code={usdtRatesResponseJson} />
        <p className="text-xs">
          Это живые курсы (те же, что видны на /admin/rates) — они не привязаны к конкретной заявке. Профит уже
          созданной заявки считается по курсам, зафиксированным при её создании (snapshot), и не меняется при
          последующем обновлении курсов.
        </p>
      </Section>

      <Section id="errors" title="7. Ошибки и лимиты">
        <p>
          Все ошибки (кроме превышения rate limit — см. ниже) возвращаются в едином конверте:
        </p>
        <CodeBlock code={'{ "error": { "code": "...", "message": "...", "details": null | any } }'} />
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>code</th>
                <th className={tableHeadCell}>HTTP</th>
                <th className={tableHeadCell}>Когда</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_CODES.map((e) => (
                <tr key={e.code} className={tableRow}>
                  <td className={tableCell}>
                    <InlineCode>{e.code}</InlineCode>
                  </td>
                  <td className={`${tableCell} whitespace-nowrap`}>{e.http}</td>
                  <td className={tableCell}>{e.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 font-medium text-text">Примеры</p>
        <CodeBlock code={validationErrorJson} />
        <CodeBlock code={badRequestErrorJson} />

        <p className="mt-4 font-medium text-text">Rate limits</p>
        <p>
          Лимиты считаются на ключ (не на IP): <InlineCode>POST /orders</InlineCode> — 120 запросов/мин;{' '}
          все GET-эндпоинты (<InlineCode>/orders/:id</InlineCode>, <InlineCode>/orders/by-external/:externalId</InlineCode>,{' '}
          <InlineCode>/accounts</InlineCode>, <InlineCode>/currencies</InlineCode>, <InlineCode>/usdt-rates</InlineCode>) — 300
          запросов/мин.
        </p>
        <Note>
          Ответ при превышении лимита (429) проходит через тот же конверт <InlineCode>{'{error:{code,message}}'}</InlineCode>,
          но это стандартный ответ библиотеки rate-limit'а, а не наш маппинг ошибок: <InlineCode>code</InlineCode> —
          несемантический (<InlineCode>"Error"</InlineCode>), поля <InlineCode>details</InlineCode> нет:
        </Note>
        <CodeBlock code={rateLimitErrorJson} />
      </Section>

      <Section id="webhooks" title="8. Webhooks">
        <p>
          Ключ можно подписать на события заявок (настраивается в модалке ключа на вкладке «Ключи» — URL + список
          событий). Доставки — at-least-once: получатель должен уметь обрабатывать повторные вызовы (используйте{' '}
          <InlineCode>deliveryId</InlineCode> для дедупликации). Ключ получает события только по своим заявкам (по
          заявкам, созданным этим же ключом).
        </p>
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Событие</th>
                <th className={tableHeadCell}>Когда</th>
              </tr>
            </thead>
            <tbody>
              {WEBHOOK_EVENT_TYPES.map((ev) => (
                <tr key={ev} className={tableRow}>
                  <td className={tableCell}>
                    <InlineCode>{ev}</InlineCode>
                  </td>
                  <td className={tableCell}>
                    {WEBHOOK_EVENT_LABELS[ev]} — {WEBHOOK_EVENT_DESCRIPTIONS[ev]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3">
          Тело запроса всегда несёт актуальное состояние заявки целиком (<InlineCode>data</InlineCode> — тот же{' '}
          <InlineCode>PublicOrderDto</InlineCode>, что и в ответах REST-эндпоинтов) — не нужно восстанавливать
          порядок событий или запрашивать заявку отдельно:
        </p>
        <CodeBlock code={webhookPayloadJson} />

        <p className="mt-3 font-medium text-text">Заголовки и подпись</p>
        <div className={tableWrapper}>
          <table className={tableCls}>
            <tbody>
              {WEBHOOK_HEADERS.map((h) => (
                <tr key={h.header} className={tableRow}>
                  <td className={`${tableCell} whitespace-nowrap`}>
                    <InlineCode>{h.header}</InlineCode>
                  </td>
                  <td className={tableCell}>{h.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 font-medium text-text">Проверка подписи (Node.js)</p>
        <CodeBlock code={webhookVerifyNodeJs} />

        <p className="mt-3 font-medium text-text">Ретраи</p>
        <p>
          Таймаут попытки — 10 секунд, успехом считается любой ответ 2xx. При ошибке (таймаут/сеть/не-2xx) —
          экспоненциальный backoff <InlineCode>min(2^попытка × 30с, 1ч)</InlineCode> (номер попытки инкрементируется
          до расчёта паузы, поэтому первая пауза уже минута: 1мин, 2мин, 4мин, 8мин, 16мин, 32мин, 1ч (кап)) и до 8
          попыток всего; после 8-й неудачи доставка помечается{' '}
          <InlineCode>failed</InlineCode> и не повторяется автоматически (повторный запуск — вручную, из журнала
          доставок на вкладке «Журнал»).
        </p>
      </Section>
    </div>
  );
}
